// Reports Dashboard Index Page - WITH REAL DATA
import { Link } from '@tanstack/react-router';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useReportsDashboardSummary } from '@/hooks/queries/useReports';
import { useAuth } from '@/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp,
  Wallet,
  DollarSign,
  FileText,
  Package,
  Users,
  ArrowRight,
} from 'lucide-react';

export const Route = createFileRoute('/apps/reports/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: ReportsIndexPage,
});

interface ReportCard {
  id: string;
  title: string;
  titleAr: string;
  description: string;
  icon: any;
  color: string;
  route: string;
  badge?: string;
}

const REPORTS: ReportCard[] = [
  {
    id: 'sales',
    title: 'Rapport des ventes',
    titleAr: 'تقرير المبيعات',
    description: 'Analyse complète des ventes, produits, et clients',
    icon: TrendingUp,
    color: 'from-emerald-500 to-teal-600',
    route: '/apps/reports/sales',
  },
  {
    id: 'expenses',
    title: 'Rapport des dépenses',
    titleAr: 'تقرير المصاريف',
    description: 'Suivi des dépenses par catégorie et fournisseur',
    icon: Wallet,
    color: 'from-orange-500 to-red-600',
    route: '/apps/reports/expenses',
  },
  {
    id: 'cashflow',
    title: 'Flux de trésorerie',
    titleAr: 'التدفق النقدي',
    description: 'Recettes, dépenses, et prévisions de trésorerie',
    icon: DollarSign,
    color: 'from-blue-500 to-cyan-600',
    route: '/apps/reports/cashflow',
  },
  {
    id: 'tax',
    title: 'Rapport TVA (G50)',
    titleAr: 'تقرير الضريبة',
    description: 'Déclaration mensuelle de TVA pour la DGI',
    icon: FileText,
    color: 'from-purple-500 to-violet-600',
    route: '/apps/reports/tax',
    badge: 'DGI',
  },
  {
    id: 'stock',
    title: 'Rapport de stock',
    titleAr: 'تقرير المخزون',
    description: 'Valeur du stock, mouvements, et ruptures',
    icon: Package,
    color: 'from-yellow-500 to-orange-600',
    route: '/apps/reports/stock',
    badge: 'SOON',
  },
  {
    id: 'clients',
    title: 'Rapport clients',
    titleAr: 'تقرير العملاء',
    description: 'Créances clients et analyse par échéance',
    icon: Users,
    color: 'from-cyan-500 to-blue-600',
    route: '/apps/reports/clients',
    badge: 'SOON',
  },
];

function ReportsIndexPage() {
  const { user } = useAuth();
  const companyId = user?.company_id || '';

  // Fetch real dashboard summary data
  const { data: summary, isLoading } = useReportsDashboardSummary(companyId);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'decimal',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format growth percentage
  const formatGrowth = (growth: number) => {
    const sign = growth >= 0 ? '+' : '';
    return `${sign}${growth.toFixed(1)}%`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            <span className="font-arabic">التقارير</span>
            <span className="text-muted-foreground mx-2">/</span>
            <span>Rapports</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            Analysez vos données et suivez la performance de votre entreprise
          </p>
        </div>

        {/* Quick Stats - WITH REAL DATA */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Sales Card */}
          <div className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              {isLoading ? (
                <Skeleton className="h-6 w-16" />
              ) : (
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  (summary?.salesGrowth || 0) >= 0
                    ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/30'
                    : 'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30'
                }`}>
                  {formatGrowth(summary?.salesGrowth || 0)}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">Ventes ce mois</p>
            {isLoading ? (
              <Skeleton className="h-8 w-32 mt-1" />
            ) : (
              <p className="text-2xl font-bold text-foreground mt-1">
                {formatCurrency(summary?.salesThisMonth || 0)} DZD
              </p>
            )}
          </div>

          {/* Expenses Card */}
          <div className="p-6 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <Wallet className="w-8 h-8 text-orange-600 dark:text-orange-400" />
              {isLoading ? (
                <Skeleton className="h-6 w-16" />
              ) : (
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  (summary?.expensesGrowth || 0) <= 0
                    ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/30'
                    : 'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30'
                }`}>
                  {formatGrowth(summary?.expensesGrowth || 0)}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">Dépenses ce mois</p>
            {isLoading ? (
              <Skeleton className="h-8 w-32 mt-1" />
            ) : (
              <p className="text-2xl font-bold text-foreground mt-1">
                {formatCurrency(summary?.expensesThisMonth || 0)} DZD
              </p>
            )}
          </div>

          {/* Cash Flow Card */}
          <div className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              {isLoading ? (
                <Skeleton className="h-6 w-16" />
              ) : (
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  summary?.cashFlowStatus === 'positive'
                    ? 'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30'
                    : 'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30'
                }`}>
                  {summary?.cashFlowStatus === 'positive' ? 'Positif' : 'Négatif'}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">Flux net</p>
            {isLoading ? (
              <Skeleton className="h-8 w-32 mt-1" />
            ) : (
              <p className="text-2xl font-bold text-foreground mt-1">
                {formatCurrency(summary?.netCashFlow || 0)} DZD
              </p>
            )}
          </div>

          {/* TVA Card */}
          <div className="p-6 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <FileText className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              <span className="text-xs font-semibold text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30 px-2 py-1 rounded-full">
                G50
              </span>
            </div>
            <p className="text-sm text-muted-foreground">TVA à payer</p>
            {isLoading ? (
              <Skeleton className="h-8 w-32 mt-1" />
            ) : (
              <p className="text-2xl font-bold text-foreground mt-1">
                {formatCurrency(summary?.tvaToPay || 0)} DZD
              </p>
            )}
          </div>
        </div>

        {/* Reports Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {REPORTS.map((report) => {
            const Icon = report.icon;
            const isAvailable = !report.badge || report.badge === 'DGI';

            const ReportCard = (
              <div
                className={`
                  relative group rounded-xl p-6 transition-all duration-300
                  ${
                    isAvailable
                      ? 'bg-card hover:shadow-lg hover:scale-105 cursor-pointer border border-border'
                      : 'bg-card cursor-not-allowed opacity-60 border border-border'
                  }
                `}
              >
                {/* Badge */}
                {report.badge && (
                  <div className="absolute top-4 right-4">
                    <span
                      className={`
                        px-2 py-1 text-xs font-bold rounded-full
                        ${
                          report.badge === 'DGI'
                            ? 'bg-purple-500 text-white'
                            : 'bg-orange-500 text-white'
                        }
                      `}
                    >
                      {report.badge}
                    </span>
                  </div>
                )}

                {/* Icon */}
                <div className="mb-4">
                  <div
                    className={`
                      w-16 h-16 rounded-xl flex items-center justify-center
                      bg-gradient-to-br ${report.color}
                      ${isAvailable ? 'group-hover:scale-110' : ''}
                      transition-transform duration-300
                    `}
                  >
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  {report.title}
                </h3>
                <p className="text-sm text-muted-foreground font-arabic mb-3">
                  {report.titleAr}
                </p>

                {/* Description */}
                <p className="text-sm text-muted-foreground mb-4">
                  {report.description}
                </p>

                {/* Arrow */}
                {isAvailable && (
                  <div className="flex items-center text-sm font-medium text-primary group-hover:gap-2 transition-all">
                    <span>Voir le rapport</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                )}

                {/* Coming Soon Overlay */}
                {!isAvailable && (
                  <div className="absolute inset-0 flex items-center justify-center bg-card/80 rounded-xl">
                    <span className="text-sm font-semibold text-muted-foreground">
                      قريباً / Bientôt
                    </span>
                  </div>
                )}
              </div>
            );

            return isAvailable ? (
              <Link key={report.id} to={report.route}>
                {ReportCard}
              </Link>
            ) : (
              <div key={report.id}>{ReportCard}</div>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Conformité fiscale algérienne
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Tous nos rapports sont conformes aux exigences de la Direction Générale des Impôts (DGI).
                Le rapport TVA (G50) est prêt pour la soumission via CNIS.
              </p>
              <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                <span className="font-semibold">Série G N°50</span>
                <span>•</span>
                <span>TVA 19%</span>
                <span>•</span>
                <span>CNIS Ready</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
